/* eslint-disable no-underscore-dangle */
/** @jsxImportSource @emotion/react */
import { useTheme } from '@emotion/react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import ErrorMessage from '@ErrorMessage';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { updateAndExposeDynamicHeightData } from '../../Utils/utils';
import AppointmentBooking from '../AppointmentBooking/AppointmentBooking';
import CpcCard from '../CpcCard/CpcCard';
import FormElement from '../FormElement/FormElement';
import { shouldAutoAdvance } from '../Page/page.helpers';
import { Transition } from '../Transition';
import useGetExperimentName from '../../Hooks/useGetExperimentName/useGetExperimentName';
import SubmissionBlockPage from '../SubmissionBlockPage/SubmissionBlockPage';
import { shouldShowSubmissionBlockPage } from '../../Helpers/submission';
import { ComponentTypes } from '../../Data/Components';

const InputArea = ({
  components,
  currentPage,
  iFrameId,
  pageComponentIds,
  slideDirection,
  visiblePageComponents,
  hasRadiosOrCheckboxesOnCurrentPage,
  onContentMounted, // added and passed by ScrollContainer to manage its state
  onContentMounting, // added and passed by ScrollContainer to manage race condition
}) => {
  // Store a local count of which page we are on so that we can check whether the page has transitioned.
  const [pageToShow, setPageToShow] = useState(currentPage);
  const isIn = pageToShow === currentPage;
  const theme = useTheme();
  const { enterPhase } = useSelector((store) => store.transition);
  const { showCpcRecommendations } = useSelector((store) => store.cpcCard);
  const { showAppointmentBooking } = useSelector(
    (store) => store.appointmentBooking
  );
  const inputAreaRef = useRef();
  const containerHeight = inputAreaRef?.current?.getBoundingClientRect().height;
  const { isDynamicHeightEnabled } = useSelector(
    (store) => store.optionalFormConfig
  );
  const { submissionBlocks } = useSelector((store) => store.submission);

  // To enable a variable widget height that dynamically changes across pages,
  // each page needs to expose its required height to fit all elements (including
  // margin onto their page area)

  const errorMessageExperimentName = useGetExperimentName({
    componentName: 'ErrorMessage',
  });

  const addDropdownHydrationLoaderStylesToHead = () => {
    const stylesToApply = `
      .dropDownLoadingDots {
        position: relative;
        width: 100vw;
        margin-top: 1%;

        span {
          content: '';
          animation: blink 1.5s infinite;
          animation-fill-mode: both;
          height: 10px;
          width: 10px;
          margin-top: 2px;
          background: grey;
          position: absolute;
          left:0;
          top:0;
          border-radius: 50%;

          &:nth-child(2) {
            animation-delay: .2s;
            margin-left: 15px;
          }

          &:nth-child(3) {
            animation-delay: .4s;
            margin-left: 30px;
          }
        }
      }

      @keyframes blink {
        0% {
          opacity: .1;
        }
        20% {
          opacity: 1;
        }
        100% {
          opacity: .1;
        }
      }
    `;
    const styleSheetId = 'mvf-dropdown-hydration-styles';
    const head =
      document.head || document.getElementsByTagName('head')
        ? document.head || document.getElementsByTagName('head')[0]
        : undefined;

    if (head) {
      const currentStyleSheet = document.getElementById(styleSheetId);
      const styleSheet = currentStyleSheet || document.createElement('style');
      styleSheet.id = styleSheetId;
      styleSheet.type = 'text/css';

      if (currentStyleSheet) {
        // overwrite pre-existing style
        document.getElementById(styleSheetId).innerHTML = stylesToApply;
      } else {
        // add a style if the stylesheet doesn't exist
        styleSheet.appendChild(document.createTextNode(stylesToApply));
        head.appendChild(styleSheet);
      }
    }
  };

  useEffect(() => {
    const visibleComponentTypes = [];
    Object.keys(components).forEach((componentIdString) => {
      const elementData = components[componentIdString];
      const { component, input } = elementData;
      const { type } = component;
      if (!input?.hiddenInChameleon) {
        visibleComponentTypes.push(type);
      }
    });
    if (visibleComponentTypes.some((type) => type === ComponentTypes.SELECT)) {
      addDropdownHydrationLoaderStylesToHead();
    }
  }, []);

  useEffect(() => {
    if (
      !enterPhase &&
      currentPage !== undefined &&
      isDynamicHeightEnabled === true
    ) {
      updateAndExposeDynamicHeightData({ inputAreaRef });
    }
    /**
     * We are not dependent on the current page number, because it doesn't update after page change is completed, it is enterPhase that is updated to false when a page change is completed.
     *
     * currentPage should not be a dependency here as there could be a race between the enterPhase and the currentPage
     */
  }, [enterPhase, isDynamicHeightEnabled]);

  useEffect(() => {
    // When a user navigates through the form we trigger a slide transition by briefly delaying the update to pageToShow thereby triggering the transition.
    /**
     * if the page has entered, ie the enterPhase is false, then set the page to be shown
     */
    if (!enterPhase) {
      setTimeout(() => {
        if (slideDirection !== undefined) {
          setPageToShow(currentPage);
        }
      }, 1);
    }
    /**
     * We are not dependent on the current page number, because it doesn't update after page change is completed, it is enterPhase that is updated to false when a page change is completed.
     *
     * currentPage should not be a dependency here as there could be a race between the enterPhase and the currentPage
     */
  }, [enterPhase, containerHeight]);

  let formElements = [];
  if (shouldShowSubmissionBlockPage(currentPage, submissionBlocks)) {
    formElements = [<SubmissionBlockPage key="submission-block-page" />];
  } else {
    formElements = pageComponentIds.map((componentId) => {
      const elementData = components[`component_${componentId}`];
      const { component, input } = elementData;
      const { type, text, subheading, wasSeen, duration } = component;

      return (
        // TODO here we are passing all the data we need down, but in some components we are also selecting from the store - investigate if / how we can and try to improve this
        <FormElement
          autoAdvance={shouldAutoAdvance(elementData, visiblePageComponents)}
          componentId={componentId}
          iFrameId={iFrameId}
          isVisible={visiblePageComponents.includes(elementData)}
          key={componentId}
          slideDirection={slideDirection}
          subheading={subheading}
          text={text}
          type={type}
          value={input?.value}
          wasSeen={wasSeen}
          duration={duration}
          additionalSettings={component?.additionalSettings}
        />
      );
    });
  }

  useEffect(() => {
    /**
     * onContentMounting is a prop which is ADDED by ScrollContainer if it is a direct parent. It is used to pause the scroll area size calculation until after the input area size has been calculated.
     *
     * InputArea may not alway be in a ScrollContainer, onContentMounting will be called on each page change
     */
    if (window) {
      if (
        !window.__private__ ||
        typeof window.__private__ !== 'object' ||
        Array.isArray(window.__private__)
      ) {
        window.__private__ = {};
      }
      if (window && !window?.__private__?.onContentMounting) {
        window.__private__.onContentMounting = onContentMounting;
      }
    }
  });

  // Override the "md" maxWidth only for desktopMedium and up, and only when we have radios or checkboxes on the current page.
  // This allows us to support tiled radio/checkbox views that appear wider on desktop without affecting smaller screens.
  const styles = hasRadiosOrCheckboxesOnCurrentPage
    ? {
        maxWidth: {
          desktopMedium: theme.breakpoints.values.lg,
        },
      }
    : {};

  return (
    <Container
      maxWidth="md"
      ref={inputAreaRef}
      data-testid="inputAreaContainer"
      disableGutters
      sx={styles}
    >
      <Transition
        currentPage={currentPage}
        onContentMounted={onContentMounted}
        isIn={isIn}
        direction={slideDirection}
        data-testid="transition"
      >
        {showCpcRecommendations !== true &&
          showAppointmentBooking !== true &&
          formElements}
        <Box marginTop={theme.mvf.spacing.medium}>
          <ErrorMessage experimentName={errorMessageExperimentName} />
        </Box>
      </Transition>
      <CpcCard />
      <AppointmentBooking />
    </Container>
  );
};

export default InputArea;
